/**
 * AuthContext — single source of truth for authentication state.
 *
 * IMPORTANT: The onAuthStateChange callback must NOT make async Supabase
 * calls (like fetching profile data). Supabase holds an internal lock while
 * the callback is executing — any Supabase call in the callback will deadlock
 * because it tries to acquire the same lock. Instead we:
 *
 *  1. Synchronously update user/session from the callback
 *  2. Set isLoading: false immediately so the app can render
 *  3. Fetch the profile in a SEPARATE effect that reacts to user changes
 *
 * This avoids the deadlock and ensures seamless loading on page refresh.
 */

import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
    type ReactNode,
} from 'react'
import { supabase } from '../lib/supabase'
import type { User, Session } from '@supabase/supabase-js'

export interface Profile {
    id: string
    user_id: string
    first_name: string
    last_name: string
    role: string
    student_tag?: string
    gems: number
}

interface AuthState {
    user: User | null
    session: Session | null
    profile: Profile | null
    isLoading: boolean
    isAuthenticated: boolean
}

interface AuthContextValue extends AuthState {
    signIn: (email: string, password: string) => Promise<any>
    signOut: () => Promise<void>
    refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

// ─── Provider ───────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({
        user: null,
        session: null,
        profile: null,
        isLoading: true,
        isAuthenticated: false,
    })
    const mountedRef = useRef(true)

    // ── fetch profile helper (called OUTSIDE of onAuthStateChange) ──
    const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('user_id', userId)
                .single()

            if (error) {
                console.error('[Auth] Error fetching profile:', error.message)
                return null
            }
            return data as Profile
        } catch (error) {
            console.error('[Auth] Error in fetchProfile:', error)
            return null
        }
    }, [])

    // ── Listen for auth state changes ──
    // The callback ONLY sets user/session synchronously.
    // Profile fetching happens in a separate effect below.
    useEffect(() => {
        mountedRef.current = true

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            if (!mountedRef.current) return

            if (session?.user) {
                setState((prev) => ({
                    ...prev,
                    user: session.user,
                    session,
                    // Keep existing profile if same user (avoids flash)
                    profile: prev.user?.id === session.user.id ? prev.profile : null,
                    isLoading: false,
                    isAuthenticated: true,
                }))
            } else {
                setState({
                    user: null,
                    session: null,
                    profile: null,
                    isLoading: false,
                    isAuthenticated: false,
                })
            }
        })

        return () => {
            mountedRef.current = false
            subscription.unsubscribe()
        }
    }, [])

    // ── Fetch profile when user changes ──
    // This runs OUTSIDE of onAuthStateChange, avoiding the Supabase lock deadlock.
    useEffect(() => {
        if (!state.user || !state.isAuthenticated) return

        // Don't refetch if we already have a profile for this user
        if (state.profile && state.profile.user_id === state.user.id) return

        let cancelled = false
        const userId = state.user.id

        fetchProfile(userId).then((profile) => {
            if (cancelled || !mountedRef.current) return
            setState((prev) => {
                // Double-check user hasn't changed while we were fetching
                if (prev.user?.id !== userId) return prev
                return { ...prev, profile }
            })
        })

        return () => {
            cancelled = true
        }
    }, [state.user?.id, state.isAuthenticated, state.profile, fetchProfile])

    // ── actions ──
    const signIn = useCallback(
        async (email: string, password: string) => {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            })

            if (error) throw error

            // onAuthStateChange will handle user/session state.
            // But we eagerly set the profile too for instant feedback.
            if (data.user) {
                const profile = await fetchProfile(data.user.id)
                setState({
                    user: data.user,
                    session: data.session,
                    profile,
                    isLoading: false,
                    isAuthenticated: true,
                })
            }

            return data
        },
        [fetchProfile],
    )

    const signOut = useCallback(async () => {
        // Set state immediately for instant UI feedback.
        // onAuthStateChange SIGNED_OUT will also fire, which is fine (idempotent).
        setState({
            user: null,
            session: null,
            profile: null,
            isLoading: false,
            isAuthenticated: false,
        })
        await supabase.auth.signOut()
    }, [])

    const refreshProfile = useCallback(async () => {
        if (state.user) {
            const profile = await fetchProfile(state.user.id)
            setState((prev) => ({ ...prev, profile }))
        }
    }, [state.user, fetchProfile])

    const value: AuthContextValue = {
        ...state,
        signIn,
        signOut,
        refreshProfile,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ─── Hook ───────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext)
    if (!ctx) {
        throw new Error('useAuth must be used within an <AuthProvider>')
    }
    return ctx
}
